import { FC, useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import {
    requestNotificationPermission,
    getNotificationPermissionStatus
} from '../../services/pushNotificationService';

interface NotificationPromptProps {
    onDismiss?: () => void;
}

export const NotificationPrompt: FC<NotificationPromptProps> = ({ onDismiss }) => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const [status, setStatus] = useState<'idle' | 'loading' | 'granted' | 'denied' | 'dismissed'>('idle');
    const [permissionState, setPermissionState] = useState<NotificationPermission | 'unsupported'>('default');

    useEffect(() => {
        const currentStatus = getNotificationPermissionStatus();
        setPermissionState(currentStatus);

        // If already granted, don't show prompt
        if (currentStatus === 'granted') {
            setStatus('granted');
        }
    }, []);

    const handleEnable = async () => {
        if (!user) return;

        setStatus('loading');
        const token = await requestNotificationPermission(user.uid);

        if (token) {
            setStatus('granted');
        } else {
            setStatus('denied');
        }
    };

    const handleDismiss = () => {
        setStatus('dismissed');
        // Store in localStorage so we don't show again for 7 days
        localStorage.setItem('notificationPromptDismissed', Date.now().toString());
        onDismiss?.();
    };

    // Don't show if notifications are unsupported
    if (permissionState === 'unsupported') return null;

    // Don't show if already granted, denied by user, or dismissed
    if (status === 'granted' || status === 'denied' || status === 'dismissed') return null;
    if (permissionState === 'denied') return null;

    // Check if recently dismissed
    const dismissedAt = localStorage.getItem('notificationPromptDismissed');
    if (dismissedAt) {
        const dismissedTime = parseInt(dismissedAt);
        const sevenDays = 7 * 24 * 60 * 60 * 1000;
        if (Date.now() - dismissedTime < sevenDays) return null;
    }

    return (
        <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-slide-up">
            <div className="module-card p-4 border-primary/30 shadow-lg">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <span className="material-symbols-outlined text-primary">notifications_active</span>
                    </div>
                    <div className="flex-1">
                        <h4 className="font-bold text-text-primary mb-1">{t('notifications.prompt.title', 'Activer les notifications ?')}</h4>
                        <p className="text-sm text-text-secondary mb-3">
                            {t('notifications.prompt.description', 'Recevez des alertes pour les défis, validations de score et messages importants.')}
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={handleEnable}
                                disabled={status === 'loading'}
                                className="px-4 py-3 min-h-[48px] bg-primary text-white text-sm font-bold rounded-lg hover:bg-primary-hover transition-colors flex items-center gap-2 disabled:opacity-50"
                            >
                                {status === 'loading' ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                        {t('notifications.prompt.activating', 'Activation...')}
                                    </>
                                ) : (
                                    <>
                                        <span className="material-symbols-outlined text-sm">check</span>
                                        {t('notifications.prompt.activate', 'Activer')}
                                    </>
                                )}
                            </button>
                            <button
                                onClick={handleDismiss}
                                className="px-4 py-3 min-h-[48px] bg-surface-2 text-text-secondary text-sm font-medium rounded-lg hover:bg-surface-3 transition-colors"
                            >
                                {t('notifications.prompt.later', 'Plus tard')}
                            </button>
                        </div>
                    </div>
                    <button
                        onClick={handleDismiss}
                        className="text-text-secondary hover:text-text-primary transition-colors p-3 min-w-[48px] min-h-[48px] flex justify-center items-center"
                    >
                        <span className="material-symbols-outlined text-sm">close</span>
                    </button>
                </div>
            </div>
        </div>
    );
};
